import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { map, Observable } from 'rxjs';
import { AutoFocusTransitionDirective } from '../../directives/auto-focus-transition.directive';
import { NameSanitizerDirective } from '../../directives/name-sanitizer.directive';
import { PanFormatterDirective } from '../../directives/pan-formatter.directive';
import { TPSError } from '../../services/api/api.models';
import { ErrorService } from '../../services/error/error.service';
import { ExternalDataService } from '../../services/external-data/external-data.service';
import { DoPaymentParameters, PaymentService } from '../../services/payment/payment.service';
import { EXPIRATION_DATE_INVALID, expirationDateValidator } from '../../validators/expiration-date-validator';
import { luhnValidator } from '../../validators/luhn-validators';
import { maxLengthWithoutSpacesValidator } from '../../validators/max-length-without-spaces-validator';
import { minLengthWithoutSpacesValidator } from '../../validators/min-length-without-spaces-validator';
import { monthFormatValidator } from '../../validators/month-format-validator';

@Component({
  selector: 'app-payment',
  imports: [ReactiveFormsModule, CommonModule, TranslateModule, AutoFocusTransitionDirective, NameSanitizerDirective, PanFormatterDirective],
  templateUrl: './payment.component.html',
  styleUrl: './payment.component.scss',
})
export class PaymentComponent implements OnInit {
  public form = new FormGroup(
    {
      pan: new FormControl('', [Validators.required, luhnValidator(), minLengthWithoutSpacesValidator(13), maxLengthWithoutSpacesValidator(19)]),
      expDateMonth: new FormControl('', [Validators.required, monthFormatValidator()]),
      expDateYear: new FormControl('', [Validators.required, Validators.pattern(/^[0-9]{2}$/)]),
      cardOwnerName: new FormControl('', Validators.required),
      cvv2: new FormControl('', [Validators.required, Validators.pattern(/^[0-9]{3}$/)]),
      register: new FormControl(false),
    },
    {
      validators: expirationDateValidator('expDateMonth', 'expDateYear'),
      updateOn: 'change',
    },
  );

  public fc = this.form.controls;

  public panCanBeInError = false;
  public expirationCanBeInError = false;
  public nameCanBeInError = false;
  public cvv2CanBeInError = false;

  get showPanError() { return this.fc.pan.invalid && this.panCanBeInError }
  get showExpirationError() { return (this.fc.expDateMonth.invalid || this.fc.expDateYear.invalid) && this.expirationCanBeInError }
  get showNameError() { return this.fc.cardOwnerName.invalid && this.nameCanBeInError }
  get showCvv2Error() { return this.fc.cvv2.invalid && this.cvv2CanBeInError }

  public $isSubmitEnabled: Observable<boolean>;
  public $panFormatting: Observable<boolean>;
  public $placeholders: Observable<boolean>;
  public $permissiveSubmitButton: Observable<boolean>;
  public $tpsErrors: Observable<TPSError[]>;

  constructor(
    private paymentService: PaymentService,
    private errorService: ErrorService,
    private externalDataService: ExternalDataService
  ) {
    this.$isSubmitEnabled = this.paymentService.$isProcessingOrProcessed.pipe(map((v) => !v));
    this.$panFormatting = this.externalDataService.$panFormatting;
    this.$placeholders = this.externalDataService.$placeholders;
    this.$permissiveSubmitButton = this.externalDataService.$permissiveSubmitButton;
    this.$tpsErrors = this.errorService.$tpsErrors;

    this.fc.pan.valueChanges.subscribe(() => this.panCanBeInError = false);
    this.fc.expDateMonth.valueChanges.subscribe(() => this.expirationCanBeInError = false);
    this.fc.expDateYear.valueChanges.subscribe(() => this.expirationCanBeInError = false);
    this.fc.cardOwnerName.valueChanges.subscribe(() => this.nameCanBeInError = false);
    this.fc.cvv2.valueChanges.subscribe(() => this.cvv2CanBeInError = false);
  }

  ngOnInit(): void {
    this.paymentService.$isProcessingOrProcessed.subscribe(isProcessingOrProcessed => {
      if (!isProcessingOrProcessed) {
        this.form?.enable();
      } else {
        this.form?.disable();
      }
    })
  }

  // Specific errors
  get expirationDateInPast() {
    return (
      (this.fc.expDateYear.dirty || this.fc.expDateYear.touched) &&
      (this.fc.expDateMonth.dirty || this.fc.expDateMonth.touched) &&
      this.form.hasError(EXPIRATION_DATE_INVALID)
    );
  }


  onSubmit() {
    this.panCanBeInError = true;    
    this.expirationCanBeInError = true; 
    this.nameCanBeInError = true;
    this.cvv2CanBeInError = true;

    if (this.form.invalid) {
      return;
    }

    this.paymentService.$isProcessingOrProcessed.next(true);
    this.errorService.clearTpsErrors();

    const formValues = {...this.form.value, pan: this.form.value.pan?.replaceAll(' ', '')};
    const params: DoPaymentParameters = {
      epan: formValues.pan!,
      expDateMonth: formValues.expDateMonth!,
      expDateYear: formValues.expDateYear!,
      cardHolderName: formValues.cardOwnerName!,
      cvv2: formValues.cvv2!,
      register: formValues.register ?? false
    }

    this.paymentService.doPayment(params);
  }
}
